import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
} from '@angular/core';
import { ButtonComponent, IconComponent, PasswordComponent } from '@app-ui';
import { AsyncPipe, NgClass, NgFor, NgIf } from '@angular/common';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

/**
 * Password card component
 */
@Component({
  selector: 'auth-password-card',
  standalone: true,
  imports: [
    PasswordComponent,
    ButtonComponent,
    AsyncPipe,
    IconComponent,
    FormsModule,
    ReactiveFormsModule,
    NgClass,
    NgFor,
    NgIf,
    TranslateModule,
  ],
  templateUrl: './password-card.component.html',
  styleUrl: './password-card.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PasswordCardComponent {
  private readonly _fb = inject(FormBuilder);
  private readonly _translate = inject(TranslateService);
  private readonly _msg = this._translate.instant(
    'auth.component.password-card.message.validation',
    { value: 2 },
  );

  @Input() loading = false;

  @Output() passwordSubmit = new EventEmitter<string>();

  strength = 0;

  fg = this._fb.group(
    {
      newPassword: [
        null,
        [
          Validators.required,
          (control: FormControl) => this._newPasswordValidator(control),
        ],
      ],
      confirmPassword: [null, [Validators.required]],
    },
    {
      validator: this._confirmPasswordValidator(
        'newPassword',
        'confirmPassword',
      ),
    },
  );

  /**
   * validator
   *
   * @param control
   */
  private _newPasswordValidator(control: FormControl) {
    const value = typeof control.value === 'string' ? control.value : '';

    this.strength =
      (value.length > 6 ? 1 : 0) +
      (value.length > 10 ? 1 : 0) +
      (/[A-Z]/.test(value) ? 1 : 0) +
      (/[0-9]/.test(value) ? 1 : 0) +
      (/[\W]/.test(value) ? 1 : 0);

    return this.strength <= 2 ? { strength: { message: this._msg } } : null;
  }

  /**
   * Confirm password validator
   *
   * @param newPwdControlName
   * @param confirmPwdControlName
   */
  private _confirmPasswordValidator(
    newPwdControlName: string,
    confirmPwdControlName: string,
  ): ValidatorFn {
    return (formGroup: AbstractControl) => {
      const newPwdControl = formGroup.get(newPwdControlName);
      const confirmPwdControl = formGroup.get(confirmPwdControlName);

      if (!newPwdControl || !confirmPwdControl) {
        return null;
      }

      return newPwdControl.value === confirmPwdControl.value
        ? null
        : { passwordMismatch: true };
    };
  }

  /**
   * Handle on submit form
   */
  changePassword() {
    if (!this.fg.valid) return;
    this.passwordSubmit.emit(this.fg.value.newPassword.trim());
  }
}
